import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import OrderTimeline, { TimelineStage } from './OrderTimeline';
import { generateTimelineStages, updateTimelineStage, getTimelineProgress } from '../utils/timelineUtils';
import { API_BASE } from '@/config/api';
import { OrderIdBadge } from './shared';
import { useAuth } from '../context/AuthContext';
import { normalizeOrderData, formatStatusForDisplay } from '../utils/dataNormalizer';
import { useLanguage } from '@/context/LanguageContext';
import { getMeasurementLabel } from '@/utils/measurementTranslations';

interface Photo {
  id: number;
  photo_type: string;
  photo_url: string;
  thumbnail_url?: string;
  scope?: string;
  created_at?: string;
}

interface Invoice {
  id: number;
  total: number;
  material_component?: number;
  stitching_component?: number;
  issued_at?: string;
  created_at?: string;
}

interface Order {
  id: string;
  order_number?: string;
  customer_name?: string;
  customer_phone?: string;
  customer?: {
    name?: string;
    email?: string;
  };
  order_type?: string;
  garmentType?: string;
  cloth_type?: string;
  price?: number;
  amount?: number;
  status: string; // Can be 'pending', 'Pending', 'in_progress', etc.
  status_raw?: string; // Raw status from backend
  priority?: 'high' | 'medium' | 'low';
  due_date?: string;
  pickup_date?: string;
  assigned_tailor?: string;
  measurements?: {
    neck?: number;
    chest?: number;
    waist?: number;
    sleeve_length?: number;
    inseam?: number;
    [key: string]: number | undefined;
  };
  photos?: Photo[];
  invoice?: Invoice;
  special_notes?: string;
  created_date?: string;
  created_at?: string;
  last_updated?: string;
  updated_at?: string;
  // Progress tracking fields
  cutting_started?: boolean;
  stitching_started?: boolean;
  fabric_selected?: boolean;
  quality_check_completed?: boolean;
  ready_for_pickup?: boolean;
}

const OrderDetail: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [timelineStages, setTimelineStages] = useState<TimelineStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);

  React.useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderNumber) {
        setError('No order number provided');
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE}/api/orders/${orderNumber}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          // Use centralized normalizer instead of inline normalization
          // This replaces the previous normalization logic (lines 107-136)
          const normalizedOrder = normalizeOrderData(data);
          
          setOrder(normalizedOrder as Order);
          
          // Generate timeline stages
          const stages = generateTimelineStages({
            orderId: normalizedOrder.order_number || normalizedOrder.id,
            orderCreatedAt: normalizedOrder.created_date || new Date().toISOString(),
            dueDate: normalizedOrder.due_date || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            currentStatus: normalizedOrder.status,
            pickupDate: normalizedOrder.pickup_date,
            completionDate: normalizedOrder.status === 'completed' ? normalizedOrder.last_updated : undefined
          });
          
          setTimelineStages(stages);
        } else {
          setError('Failed to fetch order details');
        }
      } catch (_err) {
        setError('Error fetching order details');
        console.error('Error fetching order:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderNumber]);

  const handleStatusUpdate = async (updates: {
    status?: string;
    cutting_started?: boolean;
    stitching_started?: boolean;
    fabric_selected?: boolean;
    quality_check_completed?: boolean;
    ready_for_pickup?: boolean;
  }) => {
    if (!orderNumber || !order) return;
    
    setIsUpdatingStatus(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${API_BASE}/api/orders/${orderNumber}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        // Refresh order data
        const refreshResponse = await fetch(`${API_BASE}/api/orders/${orderNumber}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          
          // Use centralized normalizer instead of inline normalization
          const normalizedOrder = normalizeOrderData(data);
          
          setOrder(normalizedOrder as Order);
          
          // Regenerate timeline
          const stages = generateTimelineStages({
            orderId: normalizedOrder.order_number || normalizedOrder.id,
            orderCreatedAt: normalizedOrder.created_date || new Date().toISOString(),
            dueDate: normalizedOrder.due_date || new Date().toISOString(),
            currentStatus: normalizedOrder.status,
            pickupDate: normalizedOrder.pickup_date,
            completionDate: normalizedOrder.status === 'completed' ? normalizedOrder.last_updated : undefined
          });
          setTimelineStages(stages);
          setShowStatusUpdate(false);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update order status');
      }
    } catch (_err) {
      setError('Error updating order status');
      console.error('Error updating order:', err);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleStageUpdate = (stageId: string, newStatus: 'completed' | 'current' | 'pending') => {
    const updatedStages = updateTimelineStage(timelineStages, stageId, newStatus);
    setTimelineStages(updatedStages);
    
    // In real app, update order status in backend
    console.log(`Updated stage ${stageId} to ${newStatus}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const normalizedStatus = (status || 'pending').toLowerCase();
    switch (normalizedStatus) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'in_progress':
      case 'in progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ready':
      case 'ready for pickup':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'completed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    // Use centralized status formatter
    return formatStatusForDisplay(status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  // Determine dashboard route based on user role
  const getDashboardRoute = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'customer':
        return '/customer/dashboard';
      case 'tailor':
        return '/business/dashboard';
      case 'admin':
        return '/admin/dashboard';
      default:
        return '/';
    }
  };

  if (error || !order) {
    const isAccessDenied = error?.includes('Access denied') || error?.includes('403');
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {isAccessDenied ? 'Access Denied' : 'Error Loading Order'}
          </h2>
          <p className="text-gray-600 mb-4">
            {isAccessDenied 
              ? 'You do not have permission to view this order. Orders can only be accessed by the assigned tailor and the customer who placed the order.'
              : (error || 'Order not found')}
          </p>
          <Link 
            to={getDashboardRoute()} 
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const progressPercentage = getTimelineProgress(timelineStages);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate(getDashboardRoute())}
                className="text-gray-400 hover:text-gray-600 mr-4"
              >
                <span className="text-2xl">←</span>
              </button>
              <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
                <span className="text-white text-lg font-bold">📋</span>
              </div>
              <span className="ml-3 text-xl font-bold text-gray-900">Order Details</span>
            </div>
            <div className="flex items-center space-x-4">
              <OrderIdBadge orderNumber={order.order_number} id={order.id} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Order Information, Measurements, and Photos */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Customer</label>
                  <p className="text-sm text-gray-900">{order.customer_name || 'Not Available'}</p>
                  <p className="text-sm text-gray-600">{order.customer_phone || 'Not Available'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Order Type</label>
                  <p className="text-sm text-gray-900">{order.garmentType || order.order_type || 'Not Available'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Cloth Type</label>
                  <p className="text-sm text-gray-900">{order.cloth_type || 'Not Available'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Price</label>
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(order.amount || order.price || 0)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(order.status)}`}>
                    {getStatusText(order.status)}
                  </span>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Priority</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(order.priority || 'medium')}`}>
                    {(order.priority || 'medium').charAt(0).toUpperCase() + (order.priority || 'medium').slice(1)}
                  </span>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Due Date</label>
                  <p className="text-sm text-gray-900">{order.due_date ? formatDate(order.due_date) : 'Not Available'}</p>
                </div>
                
                {order.assigned_tailor && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Assigned Tailor</label>
                    <p className="text-sm text-gray-900">{order.assigned_tailor}</p>
                  </div>
                )}
                
                {order.special_notes && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Special Notes</label>
                    <p className="text-sm text-gray-900">{order.special_notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Measurements */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Measurements</h3>
              <div className="grid grid-cols-2 gap-4">
                {order.measurements && Object.keys(order.measurements).length > 0 ? (
                  (() => {
                    // Filter out empty/null/zero values and display valid measurements
                    const validMeasurements = Object.entries(order.measurements).filter(
                      ([key, value]: [string, any]) => {
                        if (value == null || value === '') return false;
                        if (typeof value === 'number') return value !== 0;
                        if (typeof value === 'string') return value !== '0' && value.trim() !== '';
                        return true;
                      }
                    );
                    
                    return validMeasurements.length > 0 ? (
                      validMeasurements.map(([key, value]) => (
                        <div key={key}>
                          <label className="text-sm font-medium text-gray-500 capitalize">
                            {getMeasurementLabel(key, t)}
                          </label>
                          <p className="text-sm text-gray-900">
                            {typeof value === 'number' ? `${value} ${t('measurement.inches')}` : value}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-2">
                        <p className="text-sm text-gray-500">No measurements available</p>
                      </div>
                    );
                  })()
                ) : (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">No measurements available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Section */}
            {order.invoice && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-700">Total Amount</span>
                    <span className="text-lg font-bold text-gray-900">{formatCurrency(order.invoice.total)}</span>
                  </div>
                  
                  {order.invoice.material_component && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Material Cost</span>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(order.invoice.material_component)}</span>
                    </div>
                  )}
                  
                  {order.invoice.stitching_component && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Stitching Cost</span>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(order.invoice.stitching_component)}</span>
                    </div>
                  )}
                  
                  {order.invoice.issued_at && (
                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500">
                        Invoice Issued: {formatDate(order.invoice.issued_at)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Photos Section - Always show section, but show message if no photos */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Photos</h3>
              {order.photos && order.photos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {order.photos.map((photo) => (
                    <div key={photo.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="aspect-video bg-gray-100 relative">
                        <img
                          src={photo.photo_url || photo.thumbnail_url}
                          alt={`${photo.photo_type || 'photo'} view`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = `<div class="w-full h-full flex items-center justify-center text-gray-400"><span>Photo not available</span></div>`;
                            }
                          }}
                        />
                      </div>
                      <div className="p-2 bg-gray-50">
                        <p className="text-xs font-medium text-gray-700 capitalize">
                          {(photo.photo_type || 'photo').replace('_', ' ')} View
                        </p>
                        {photo.created_at && (
                          <p className="text-xs text-gray-500">
                            {new Date(photo.created_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">No photos uploaded yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="lg:col-span-2">
            {/* Status Update Section for Tailors */}
            {user?.role === 'tailor' && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Order Progress</h3>
                
                <div className="space-y-3">
                  {/* Fabric Selection */}
                  <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={order.fabric_selected || false}
                      onChange={(e) => handleStatusUpdate({ fabric_selected: e.target.checked })}
                      disabled={isUpdatingStatus}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">Fabric Selected</span>
                      <p className="text-xs text-gray-500">Fabric/material has been selected for the order</p>
                    </div>
                    {order.fabric_selected && (
                      <span className="text-xs text-green-600 font-medium">✓ Done</span>
                    )}
                  </label>

                  {/* Cutting Started */}
                  <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={order.cutting_started || false}
                      onChange={(e) => handleStatusUpdate({ cutting_started: e.target.checked })}
                      disabled={isUpdatingStatus}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">Cutting Started</span>
                      <p className="text-xs text-gray-500">Fabric cutting has begun</p>
                    </div>
                    {order.cutting_started && (
                      <span className="text-xs text-green-600 font-medium">✓ Done</span>
                    )}
                  </label>

                  {/* Stitching Started */}
                  <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={order.stitching_started || false}
                      onChange={(e) => handleStatusUpdate({ stitching_started: e.target.checked })}
                      disabled={isUpdatingStatus}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">Stitching Started</span>
                      <p className="text-xs text-gray-500">Garment stitching has begun</p>
                    </div>
                    {order.stitching_started && (
                      <span className="text-xs text-green-600 font-medium">✓ Done</span>
                    )}
                  </label>

                  {/* Quality Check */}
                  <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={order.quality_check_completed || false}
                      onChange={(e) => handleStatusUpdate({ quality_check_completed: e.target.checked })}
                      disabled={isUpdatingStatus}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">Quality Check Completed</span>
                      <p className="text-xs text-gray-500">Final quality inspection has been completed</p>
                    </div>
                    {order.quality_check_completed && (
                      <span className="text-xs text-green-600 font-medium">✓ Done</span>
                    )}
                  </label>

                  {/* Ready for Pickup */}
                  <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={order.ready_for_pickup || false}
                      onChange={(e) => handleStatusUpdate({ ready_for_pickup: e.target.checked })}
                      disabled={isUpdatingStatus}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">Ready for Pickup</span>
                      <p className="text-xs text-gray-500">Order is ready for customer pickup or delivery</p>
                    </div>
                    {order.ready_for_pickup && (
                      <span className="text-xs text-green-600 font-medium">✓ Done</span>
                    )}
                  </label>

                  {/* Order Complete */}
                  <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors border-t border-gray-200 pt-3 mt-1">
                    <input
                      type="checkbox"
                      checked={order.status === 'completed'}
                      onChange={(e) => handleStatusUpdate({ status: e.target.checked ? 'completed' : 'pending' })}
                      disabled={isUpdatingStatus}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">Order Completed</span>
                      <p className="text-xs text-gray-500">Order has been completed and delivered/picked up</p>
                    </div>
                    {order.status === 'completed' && (
                      <span className="text-xs text-green-600 font-medium">✓ Done</span>
                    )}
                  </label>

                  {isUpdatingStatus && (
                    <div className="pt-2">
                      <p className="text-sm text-gray-500 text-center">Updating status...</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Timeline Component */}
            <OrderTimeline
              orderId={order.order_number || order.id}
              orderCreatedAt={order.created_date || new Date().toISOString()}
              dueDate={order.due_date || new Date().toISOString()}
              currentStatus={getStatusText(order.status)}
              stages={timelineStages}
              onStageUpdate={handleStageUpdate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
